<template>
  <section class="hero is-flex main">
    <div class="bg-img" aria-hidden="true">
      <div class="img2 icon"><img src="../assets/images/book.png" alt="" /></div>
      <div class="img3 icon"><img src="../assets/images/rocket.png" alt="" /></div>
      <div class="img4 icon"><img src="../assets/images/cloud3.png" alt="" /></div>
      <div class="img5 icon"><img src="../assets/images/award.png" alt="" /></div>
      <div class="img6 icon"><img src="../assets/images/apple.png" alt="" /></div>
    </div>

    <b-loading :is-full-page="false" :active.sync="isLoading" />

    <div class="title">{{ headerTitle }}</div>
    <p class="school-subtitle" v-if="getUserSchoolName">
      <span class="school-tag">School</span> {{ getUserSchoolName }}
    </p>

    <!-- Admin / Teacher view: management cards -->
    <div v-if="isStaff" class="school-dashboard">
      <div class="school-grid">
        <div
          v-for="(card, i) in staffCards"
          :key="i"
          class="school-card"
          :style="`--card-color: ${card.color}`"
          @click="goTo(card.route)"
          role="button"
          tabindex="0"
          @keyup.enter="goTo(card.route)"
        >
          <div class="school-card-icon">
            <img :src="card.img" :alt="card.name" />
          </div>
          <div class="school-card-body">
            <div class="school-card-title">{{ card.name }}</div>
            <div class="school-card-desc">{{ card.description }}</div>
          </div>
          <div class="school-card-arrow" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
          </div>
        </div>
      </div>
    </div>

    <!-- Parent view: kid cards + classroom panel -->
    <div v-else-if="isParent" class="parent-dashboard">
      <div v-if="!isLoading && fetchKids.length" class="kids-grid">
        <div
          v-for="(card, i) in parentKidCards"
          :key="`kid-${i}`"
          class="kid-card"
          :style="`background: linear-gradient(135deg, ${card.color}25, ${card.color}45)`"
          @click="$router.push({ name: 'kiddetail_practice', params: { kid_id: card._id } })"
        >
          <div class="kid-avatar" :style="`background: linear-gradient(135deg, ${card.color}, ${shadeColor(card.color, 20)})`">
            <span>{{ initials(card.name) }}</span>
          </div>
          <div class="kid-info">
            <div class="kid-name">{{ card.name }}</div>
            <div class="kid-subtitle">Tap to start practicing</div>
          </div>
          <div class="kid-actions">
            <button
              class="kid-action-btn"
              @click.stop="$router.push({ name: 'kiddetail_analytics', params: { kid_id: card._id } })"
              title="Analytics"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="20" x2="18" y2="10"></line><line x1="12" y1="20" x2="12" y2="4"></line><line x1="6" y1="20" x2="6" y2="14"></line></svg>
            </button>
          </div>
        </div>
      </div>

      <div v-else-if="!isLoading" class="empty-card">
        <div class="empty-emoji">👶</div>
        <div class="empty-title">No kids linked yet</div>
        <div class="empty-text">Your school admin will add a child to your account shortly.</div>
      </div>

      <div v-if="hasClassroom" class="classroom-panel">
        <div class="classroom-panel-head">
          <div class="classroom-panel-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-6 9 6v11a2 2 0 0 1-2 2h-4v-7H9v7H5a2 2 0 0 1-2-2V9z"></path></svg>
          </div>
          <div>
            <div class="classroom-panel-label">Your child's classroom</div>
            <div class="classroom-panel-title">{{ classroomInfo.name }}</div>
            <div class="classroom-panel-sub" v-if="classroomInfo.section">Section {{ classroomInfo.section }}</div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'SchoolHome',
  data() {
    return {
      isLoading: false,
      classroomInfo: { name: '', section: null }
    }
  },
  computed: {
    ...mapGetters([
      'getUserType',
      'getUserSchoolName',
      'getTeacherToken',
      'fetchKids',
      'fetchColors',
      'fetchClassroom'
    ]),
    isStaff() {
      return ['SA', 'ST', 'A'].includes(this.getUserType)
    },
    isParent() {
      return this.getUserType === 'P'
    },
    headerTitle() {
      if (this.isParent) return 'Welcome'
      return 'Dashboard'
    },
    staffCards() {
      const all = [
        {
          name: 'Manage Accounts',
          description: 'Add and manage teachers and students',
          img: require('../assets/images/user.svg'),
          route: 'manage-account',
          color: 'rgb(137 25 231)',
          roles: ['SA']
        },
        {
          name: 'Manage Classrooms',
          description: 'Create classrooms and assign people',
          img: require('../assets/images/user.svg'),
          route: 'classrooms',
          color: 'rgb(49 200 255)',
          roles: ['SA', 'ST']
        }
      ]
      return all.filter(c => !c.roles || c.roles.includes(this.getUserType))
    },
    parentKidCards() {
      const colors = this.fetchColors || []
      return (this.fetchKids || []).map((kid, idx) => ({
        _id: kid._id,
        name: kid.name,
        color: colors[colors.length - 1 - idx] || '#8919e7'
      }))
    },
    hasClassroom() {
      return !!(this.classroomInfo && this.classroomInfo.name)
    }
  },
  methods: {
    ...mapActions(['loadKids', 'loadClassroom']),
    goTo(name) {
      if (name) this.$router.push({ name })
    },
    initials(name) {
      if (!name) return '?'
      const parts = name.trim().split(/\s+/)
      const a = parts[0] ? parts[0][0] : ''
      const b = parts[1] ? parts[1][0] : ''
      return (a + b).toUpperCase() || '?'
    },
    shadeColor(color, percent) {
      try {
        const R = parseInt(color.substring(1, 3), 16)
        const G = parseInt(color.substring(3, 5), 16)
        const B = parseInt(color.substring(5, 7), 16)
        const r = Math.min(255, Math.round((R * (100 + percent)) / 100))
        const g = Math.min(255, Math.round((G * (100 + percent)) / 100))
        const b = Math.min(255, Math.round((B * (100 + percent)) / 100))
        const toHex = n => n.toString(16).padStart(2, '0')
        return `#${toHex(r)}${toHex(g)}${toHex(b)}`
      } catch (_) {
        return color
      }
    },
    async loadParentContext() {
      this.isLoading = true
      try {
        await this.loadKids({ token: this.getTeacherToken })

        // Pull classroom info from localStorage (set on login) or from any kid's classroom
        const lsName = localStorage.getItem('user-classroom-name')
        const lsSection = localStorage.getItem('user-classroom-section')
        if (lsName) {
          this.classroomInfo = { name: lsName, section: lsSection || null }
        }
      } catch (e) {
        // silent
      } finally {
        this.isLoading = false
      }
    }
  },
  created() {
    if (this.isParent) {
      this.loadParentContext()
    }
  }
}
</script>

<style scoped>
.flex {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.bg-img {
  display: flex;
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
}
.bg-img div {
  display: flex;
  position: absolute;
  width: auto;
}
.bg-img .icon {
  max-width: 7vw;
  height: auto;
  opacity: 0.85;
}
.bg-img .img2 { top: 50%; right: 13.5%; }
.bg-img .img3 { top: 66.33%; left: 21.7%; }
.bg-img .img4 { top: 36.33%; right: 7.625%; }
.bg-img .img5 { top: 54.44%; left: 7.4375%; }
.bg-img .img6 { top: 51.67%; left: 18.6%; }

.main {
  align-items: center;
  margin: 0;
  position: relative;
  gap: 0.5rem;
  padding: clamp(1rem, 2.5vw, 2rem) clamp(0.75rem, 2vw, 1.5rem) clamp(2rem, 5vw, 3.5rem);
  min-height: auto;
}

.main::after {
  content: '';
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  height: 40vh;
  max-height: 420px;
  background-image: url(../assets/images/cloud2.png);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: bottom center;
  pointer-events: none;
  z-index: 1;
}

.title {
  position: relative;
  z-index: 2;
  font-size: clamp(1.75rem, 3.2vw, 2.6rem) !important;
  font-weight: 800 !important;
  letter-spacing: -0.02em;
  margin: 0 !important;
  text-align: center;
  background: linear-gradient(135deg, #8919e7 0%, #4361ee 60%, #31c8ff 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  line-height: 1.1;
}

.school-subtitle {
  position: relative;
  z-index: 2;
  font-size: 1rem;
  color: #475569;
  margin: 0 0 1rem;
  text-align: center;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.school-tag {
  background: rgba(137, 25, 231, 0.1);
  color: #8919e7;
  padding: 0.18rem 0.55rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

/* Staff cards */
.school-dashboard {
  position: relative;
  z-index: 2;
  width: 100%;
  margin: 0 auto;
  padding: 0 0.5rem;
}
.school-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.9rem;
  width: 100%;
}
@media screen and (max-width: 700px) {
  .school-grid { grid-template-columns: 1fr; gap: 0.75rem; }
}

.school-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1.4rem 1.4rem 1.25rem;
  min-height: 180px;
  background: #ffffff;
  border-radius: 18px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
  cursor: pointer;
  overflow: hidden;
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
  outline: none;
}
.school-card::before {
  content: '';
  position: absolute;
  top: 0; left: 0;
  width: 100%; height: 4px;
  background: var(--card-color, #8919e7);
  transition: height 0.25s ease;
}
.school-card::after {
  content: '';
  position: absolute;
  bottom: -60px; right: -60px;
  width: 160px; height: 160px;
  border-radius: 50%;
  background: var(--card-color, #8919e7);
  opacity: 0.07;
  transition: transform 0.4s ease, opacity 0.25s ease;
  pointer-events: none;
}
.school-card:hover, .school-card:focus-visible {
  transform: translateY(-5px);
  box-shadow: 0 18px 36px rgba(15, 23, 42, 0.12);
  border-color: var(--card-color, #8919e7);
}
.school-card:hover::before, .school-card:focus-visible::before { height: 6px; }
.school-card:hover::after, .school-card:focus-visible::after { transform: scale(1.3); opacity: 0.13; }

.school-card-icon {
  flex: 0 0 auto;
  width: 56px; height: 56px;
  border-radius: 16px;
  background: linear-gradient(135deg, var(--card-color, #8919e7), rgba(255, 255, 255, 0.25));
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.14);
  position: relative; z-index: 1;
}
.school-card-icon img {
  width: 30px; height: 30px;
  object-fit: contain;
  filter: brightness(0) invert(1) drop-shadow(0 1px 2px rgba(0, 0, 0, 0.15));
}
.school-card-body {
  flex: 1 1 auto; min-width: 0;
  position: relative; z-index: 1;
  display: flex; flex-direction: column; gap: 0.3rem;
}
.school-card-title {
  font-size: 1.15rem; font-weight: 700; color: #1f2937;
  margin: 0; line-height: 1.25; letter-spacing: -0.01em;
}
.school-card-desc {
  font-size: 0.875rem; color: #64748b; line-height: 1.4;
}
.school-card-arrow {
  position: absolute; top: 1.2rem; right: 1.2rem;
  display: flex; align-items: center; justify-content: center;
  width: 32px; height: 32px;
  border-radius: 50%;
  background: rgba(15, 23, 42, 0.04);
  color: #94a3b8;
  z-index: 1;
  transition: background 0.25s ease, color 0.25s ease, transform 0.25s ease;
}
.school-card:hover .school-card-arrow,
.school-card:focus-visible .school-card-arrow {
  background: var(--card-color, #8919e7);
  color: #ffffff;
  transform: translateX(2px) translateY(-2px);
}

/* Parent dashboard */
.parent-dashboard {
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.kids-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1rem;
}

.kid-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 1.1rem 1.25rem;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.06);
}
.kid-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.1);
}

.kid-avatar {
  flex: 0 0 auto;
  width: 56px; height: 56px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  color: #ffffff;
  font-weight: 700;
  font-size: 1.1rem;
  box-shadow: 0 6px 14px rgba(15, 23, 42, 0.16);
}
.kid-avatar span { letter-spacing: 0.02em; }

.kid-info { flex: 1 1 auto; min-width: 0; }
.kid-name {
  font-size: 1.15rem; font-weight: 700;
  color: #1f2937; line-height: 1.2;
  text-transform: capitalize;
}
.kid-subtitle {
  font-size: 0.85rem; color: #475569;
  margin-top: 0.15rem;
}

.kid-actions {
  display: flex; gap: 0.35rem;
}
.kid-action-btn {
  width: 32px; height: 32px;
  border-radius: 8px;
  border: 1px solid rgba(15, 23, 42, 0.1);
  background: #ffffff;
  color: #475569;
  display: inline-flex;
  align-items: center; justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}
.kid-action-btn:hover {
  background: #1f2937;
  color: #ffffff;
  border-color: #1f2937;
}

.empty-card {
  background: #ffffff;
  border: 1px dashed rgba(15, 23, 42, 0.15);
  border-radius: 16px;
  padding: 2rem 1.25rem;
  text-align: center;
}
.empty-emoji { font-size: 2rem; }
.empty-title {
  font-size: 1.1rem; font-weight: 700; color: #1f2937;
  margin: 0.4rem 0 0.2rem;
}
.empty-text { color: #64748b; font-size: 0.9rem; }

.classroom-panel {
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 16px;
  padding: 1rem 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.85rem;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.05);
}
.classroom-panel-head {
  display: flex; align-items: center; gap: 0.85rem; width: 100%;
}
.classroom-panel-icon {
  width: 44px; height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #8919e7, #4361ee);
  color: #ffffff;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 6px 14px rgba(15, 23, 42, 0.15);
}
.classroom-panel-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  font-weight: 700;
}
.classroom-panel-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1f2937;
  margin-top: 0.1rem;
}
.classroom-panel-sub {
  font-size: 0.85rem;
  color: #475569;
}
</style>
